import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} Diffuser
 * @property {number} product_id
 * @property {string} short_title
 * @property {string} hash
 * @property {Array<{id:number, option1:string, price:number, compare_at_price:number, image:string, selling_plan_id:number|null}>} variants
 * @property {Array<{type:'image'|'video', src:string, thumbnail?:string}>} media
 */

/**
 * @typedef {Object} Scent
 * @property {number} product_id
 * @property {number} variant_id
 * @property {string} title
 * @property {string} subtitle
 * @property {number} price
 * @property {number} compare_at_price
 * @property {string} image
 * @property {number|null} selling_plan_id
 */

/**
 * @typedef {Object} BysRefs
 * @property {HTMLElement} diffuserGrid
 * @property {HTMLElement} slots
 * @property {HTMLElement} heroViewport
 * @property {HTMLElement} heroTrack
 * @property {HTMLElement} heroNav
 * @property {HTMLButtonElement} heroPrev
 * @property {HTMLButtonElement} heroNext
 * @property {HTMLElement} saveBadge
 * @property {HTMLElement} saveAmount
 * @property {HTMLElement} totalPrice
 * @property {HTMLElement} totalCompare
 * @property {HTMLButtonElement} checkoutBtn
 * @property {HTMLElement} checkoutLabel
 * @property {HTMLElement} [checkoutMeta]
 * @property {HTMLElement} [subscriptionBox]
 * @property {HTMLInputElement} [subscriptionCheckbox]
 * @property {HTMLDialogElement} [swapDialog]
 */

/**
 * @extends {Component<BysRefs>}
 */
class AromelyBuildYourSet extends Component {
  /** @type {Diffuser[]} */
  #diffusers = [];

  /** @type {Scent[]} */
  #scents = [];

  /** @type {{product_id:number, variant_id:number}|null} */
  #gift = null;

  /** @type {number} */
  #activeDiffuserIndex = 0;

  /** @type {number} */
  #activeVariantIndex = 0;

  /** @type {Array<Scent|null>} */
  #slots = [];

  /** @type {number} */
  #currentSlotIndex = 0;

  /** @type {string} */
  #moneyFormat = '${{amount}}';

  /** @type {string} */
  #currency = 'USD';

  /**
   * Every code the offer needs on the cart, parsed from the comma-separated
   * setting. They go on together through cart/update.js, which takes a
   * comma-joined list. The old /discount/CODE redirect carried exactly one, so
   * it could not express an offer built from more than a single code.
   * @type {string[]}
   */
  #discountCodes = [];

  /** @type {number} */
  #diffuserDiscountPct = 0;

  /** @type {number} */
  #fragranceDiscountPct = 0;

  /** @type {string} */
  #freeLabel = 'FREE';

  /** @type {boolean} */
  #subscriptionEnabled = true;

  /** @type {boolean} */
  #subscriptionToggleAvailable = false;

  /** @type {number} */
  #subscriptionDiscountPct = 0;

  /** @type {number} */
  #fragranceCount = 3;

  /** @type {boolean} */
  #dialogDragInitialized = false;

  connectedCallback() {
    super.connectedCallback();

    this.#moneyFormat = this.dataset.moneyFormat || this.#moneyFormat;
    this.#currency = this.dataset.currency || this.#currency;
    this.#discountCodes = AromelyBuildYourSet.#parseCodes(this.dataset.discountCodes);
    this.#diffuserDiscountPct = parseInt(this.dataset.diffuserDiscountPct || '0', 10) || 0;
    this.#fragranceDiscountPct = parseInt(this.dataset.fragranceDiscountPct || '0', 10) || 0;
    this.#freeLabel = this.dataset.tFree || this.#freeLabel;
    this.#subscriptionToggleAvailable = this.dataset.enableSubscriptionToggle === 'true';
    this.#subscriptionDiscountPct = parseInt(this.dataset.subscriptionDiscountPct || '0', 10) || 0;
    this.#fragranceCount = parseInt(this.dataset.fragranceCount || '3', 10) || 3;

    this.#hydrateDiffusers();
    this.#hydrateScents();
    this.#hydrateSlots();
    this.#hydrateGift();
    this.#applyHashRouting();
    this.#renderHeroCarousel();
    this.#updateTotal();
    this.#updateSubscriptionBoxVisibility();
    this.#updateNavState();
    this.#initDropdownPanels();
    this.#initSubscriptionToggle();
  }

  // ── Hydration ─────────────────────────────────────────────────────────────

  #hydrateDiffusers() {
    const scripts = this.querySelectorAll('[data-bys-diffuser]');
    this.#diffusers = Array.from(scripts).map(s => {
      try { return JSON.parse(s.textContent || '{}'); } catch { return null; }
    }).filter(Boolean);
  }

  #hydrateScents() {
    const scripts = this.querySelectorAll('[data-bys-scent]');
    this.#scents = Array.from(scripts).map(s => {
      try { return JSON.parse(s.textContent || '{}'); } catch { return null; }
    }).filter(Boolean);
  }

  #hydrateGift() {
    const script = this.querySelector('[data-bys-gift]');
    if (!script) return;
    try { this.#gift = JSON.parse(script.textContent || 'null'); } catch { /* empty */ }
  }

  #hydrateSlots() {
    // Slots are pre-rendered server-side from the first N scents — read their
    // current state into JS so subsequent swaps update DOM + state in lockstep.
    this.#slots = [];
    const slotEls = this.refs.slots?.querySelectorAll('.aromely-bys__slot') || [];
    slotEls.forEach((el, i) => {
      const variantId = parseInt(el.getAttribute('data-variant-id') || '0', 10);
      const productId = parseInt(el.getAttribute('data-product-id') || '0', 10);
      const fromScents = this.#scents.find(s => s.variant_id === variantId)
        || this.#scents.find(s => s.product_id === productId)
        || this.#scents[i]
        || null;
      this.#slots.push(fromScents);
    });
  }

  #applyHashRouting() {
    const key = (window.location.hash || '').replace(/^#/, '').trim().toLowerCase();
    if (!key) return;
    const idx = this.#diffusers.findIndex(d => (d.hash || '').toLowerCase() === key);
    if (idx >= 0) {
      this.#activeDiffuserIndex = idx;
      this.#applyActiveDiffuserClass();
    }
  }

  // ── Diffuser selection ────────────────────────────────────────────────────

  selectDiffuser(event) {
    const card = event.target.closest('[data-diffuser-index]');
    if (!card) return;

    const idx = parseInt(card.getAttribute('data-diffuser-index'), 10) || 0;
    if (idx === this.#activeDiffuserIndex) return;

    this.#activeDiffuserIndex = idx;
    this.#activeVariantIndex = 0;
    this.#applyActiveDiffuserClass();

    // Update URL hash so the choice survives reloads / sharing
    const hash = this.#diffusers[idx]?.hash;
    if (hash) {
      const url = `${window.location.pathname}${window.location.search}#${encodeURIComponent(hash)}`;
      history.replaceState(null, '', url);
    }

    this.#renderHeroCarousel();
    this.#updateDiffuserCard(idx);
    this.#applyActiveSwatchClass();
    this.#updateTotal();
  }

  /**
   * Variant swatch click — switches the active variant on a specific
   * diffuser card. Also activates that diffuser if it isn't already.
   */
  selectVariant(event) {
    // Don't double-fire the parent card's selectDiffuser handler.
    event?.stopPropagation();
    const btn = event.target.closest('[data-variant-index]');
    if (!btn) return;

    const diffIdx = parseInt(btn.getAttribute('data-diffuser-index'), 10) || 0;
    const variantIdx = parseInt(btn.getAttribute('data-variant-index'), 10) || 0;

    const diffuserChanged = diffIdx !== this.#activeDiffuserIndex;
    this.#activeDiffuserIndex = diffIdx;
    this.#activeVariantIndex = variantIdx;
    this.#applyActiveDiffuserClass();
    this.#applyActiveSwatchClass();
    this.#updateDiffuserCard(diffIdx);

    if (diffuserChanged) {
      const hash = this.#diffusers[diffIdx]?.hash;
      if (hash) {
        const url = `${window.location.pathname}${window.location.search}#${encodeURIComponent(hash)}`;
        history.replaceState(null, '', url);
      }
    }

    this.#renderHeroCarousel();
    this.#updateTotal();
  }

  /**
   * Keyboard activation for the diffuser card (it's a div with role="button").
   * @param {KeyboardEvent} event
   */
  handleDiffKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.selectDiffuser(event);
  }

  #applyActiveDiffuserClass() {
    this.querySelectorAll('.aromely-bys__diff-card').forEach((el, i) => {
      el.classList.toggle('is-active', i === this.#activeDiffuserIndex);
    });
  }

  #applyActiveSwatchClass() {
    this.querySelectorAll('.aromely-bys__swatch').forEach(btn => {
      const dIdx = parseInt(btn.getAttribute('data-diffuser-index'), 10) || 0;
      const vIdx = parseInt(btn.getAttribute('data-variant-index'), 10) || 0;
      const isActive = dIdx === this.#activeDiffuserIndex && vIdx === this.#activeVariantIndex;
      btn.classList.toggle('is-active', isActive);
    });
  }

  /**
   * Refresh the diffuser card's image + price line for the current
   * activeVariantIndex. Re-applies the diffuser display discount.
   */
  #updateDiffuserCard(diffIdx) {
    const diffuser = this.#diffusers[diffIdx];
    if (!diffuser) return;
    const variant = diffuser.variants?.[this.#activeVariantIndex] || diffuser.variants?.[0];
    if (!variant) return;

    const card = this.querySelector(`.aromely-bys__diff-card[data-diffuser-index="${diffIdx}"]`);
    if (!card) return;

    const img = card.querySelector('[data-diff-img]');
    if (img instanceof HTMLImageElement && variant.image) img.src = variant.image;

    const orig = variant.price;
    let final = orig;
    if (this.#diffuserDiscountPct > 0) {
      final = Math.round(final * (1 - this.#diffuserDiscountPct / 100));
    }
    // Subscription discount applies only to subscribable lines (those with a
    // selling plan). The diffuser is excluded unless it actually has one,
    // matching what checkout attaches.
    if (this.#subscriptionEnabled && this.#subscriptionDiscountPct > 0 && variant.selling_plan_id) {
      final = Math.round(final * (1 - this.#subscriptionDiscountPct / 100));
    }
    let compareValue = 0;
    if (final < orig) {
      compareValue = orig;
    } else if (variant.compare_at_price > variant.price) {
      compareValue = variant.compare_at_price;
    }

    const priceEl = card.querySelector('[data-diff-price]');
    if (priceEl) priceEl.textContent = this.#formatPrice(final);

    const compareEl = card.querySelector('[data-diff-compare]');
    if (compareEl) {
      if (compareValue > 0) {
        compareEl.textContent = formatMoney(compareValue, this.#moneyFormat, this.#currency);
        compareEl.classList.remove('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hidden');
      }
    }
  }

  // ── Hero carousel ─────────────────────────────────────────────────────────

  #renderHeroCarousel() {
    const track = this.refs.heroTrack;
    const viewport = this.refs.heroViewport;
    const nav = this.refs.heroNav;
    if (!track || !viewport) return;

    track.innerHTML = '';
    viewport.scrollLeft = 0;

    const diffuser = this.#diffusers[this.#activeDiffuserIndex];
    if (!diffuser) return;

    const media = (diffuser.media || []).filter(m => m && m.src);

    if (media.length === 0) {
      // Fall back to the active variant's image as a single slide.
      const v = diffuser.variants?.[this.#activeVariantIndex] || diffuser.variants?.[0];
      const src = v?.image || '';
      track.innerHTML = `<div class="aromely-bys__slide"><img src="${src}" alt="${this.#escapeAttr(diffuser.short_title)}" loading="lazy"></div>`;
    } else {
      track.innerHTML = media.map(m => {
        if (m.type === 'video') {
          const thumb = m.thumbnail
            ? `<img src="${m.thumbnail}" alt="" class="bys-video-thumb" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:1;">`
            : '';
          return `<div class="aromely-bys__slide">${thumb}<video src="${m.src}" autoplay muted loop playsinline style="position:relative;z-index:2;"></video></div>`;
        }
        return `<div class="aromely-bys__slide"><img src="${m.src}" alt="${this.#escapeAttr(diffuser.short_title)}" loading="lazy"></div>`;
      }).join('');

      // Hide thumbnail once the video has frames so we don't cover a playing video.
      // Also force-play: setting `autoplay` via innerHTML doesn't always start
      // playback (Safari + some Chrome configs), so we call .play() explicitly.
      // The promise-rejection catch swallows NotAllowedError on browsers that
      // still block (autoplay policy quirks).
      track.querySelectorAll('video').forEach(v => {
        const thumb = v.parentElement?.querySelector('.bys-video-thumb');
        if (thumb) v.addEventListener('loadeddata', () => { thumb.style.display = 'none'; }, { once: true });
        v.muted = true;
        v.playsInline = true;
        const playPromise = v.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      });
    }

    if (nav) nav.hidden = media.length <= 1;
    this.#updateNavState();
  }

  heroPrev(event) {
    event?.preventDefault();
    const v = this.refs.heroViewport;
    if (v) v.scrollBy({ left: -v.clientWidth, behavior: 'smooth' });
  }

  heroNext(event) {
    event?.preventDefault();
    const v = this.refs.heroViewport;
    if (v) v.scrollBy({ left: v.clientWidth, behavior: 'smooth' });
  }

  #updateNavState() {
    const v = this.refs.heroViewport;
    const prev = this.refs.heroPrev;
    const next = this.refs.heroNext;
    if (!v || !prev || !next) return;
    const slideCount = v.querySelectorAll('.aromely-bys__slide').length;
    if (slideCount <= 1) {
      prev.disabled = true;
      next.disabled = true;
      return;
    }
    const idx = Math.round(v.scrollLeft / Math.max(1, v.clientWidth));
    prev.disabled = idx <= 0;
    next.disabled = idx >= slideCount - 1;

    if (!v.dataset.scrollBound) {
      v.addEventListener('scroll', () => this.#updateNavState(), { passive: true });
      v.dataset.scrollBound = '1';
    }
  }

  // ── Swap dialog ───────────────────────────────────────────────────────────

  /**
   * Open the picker dialog for a specific slot. Triggered by each slot's
   * "Swap" button — we record which slot fired the request so the eventual
   * pick (toggleCompanion) writes back to the right slot.
   */
  openSwap(event) {
    const btn = event?.target?.closest('[data-slot-index]');
    if (!btn) return;
    this.#currentSlotIndex = parseInt(btn.getAttribute('data-slot-index'), 10) || 0;

    const dialog = this.refs.swapDialog;
    if (!dialog) return;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    this.#syncDialogSelection();

    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) this.closeSwapDialog();
    }, { once: true });

    if (!this.#dialogDragInitialized) {
      this.#initDragToClose();
      this.#dialogDragInitialized = true;
    }
  }

  closeSwapDialog() {
    const dialog = this.refs.swapDialog;
    if (!dialog || !dialog.open) return;
    dialog.setAttribute('data-closing', '');
    setTimeout(() => {
      dialog.close();
      dialog.removeAttribute('data-closing');
      document.body.style.overflow = '';
    }, 250);
  }

  /**
   * Reflect the open slot's currently-assigned scent as the active option in
   * the shared picker dialog. The dialog markup is static — the first row is
   * marked data-selected server-side and single-select picking never updates
   * it — so without this every slot would open with the first option
   * highlighted instead of the one already in that slot. Matches by variant
   * id first (exact), then product id (covers non-default variants).
   */
  #syncDialogSelection() {
    const dialog = this.refs.swapDialog;
    if (!dialog) return;
    const current = this.#slots[this.#currentSlotIndex];
    const options = dialog.querySelectorAll('.aromely-scent-picker__option');
    let activeOption = null;
    for (const opt of options) {
      opt.removeAttribute('data-selected');
      if (!current || activeOption) continue;
      const variantId = parseInt(opt.getAttribute('data-variant-id') || '0', 10);
      const productId = parseInt(opt.getAttribute('data-product-id') || '0', 10);
      if (variantId === current.variant_id || productId === current.product_id) {
        activeOption = opt;
      }
    }
    if (!activeOption) return;
    activeOption.setAttribute('data-selected', '');
    activeOption.scrollIntoView({ block: 'nearest' });
  }

  /**
   * Fired by the dialog snippet's option rows. The dialog is in single-select
   * mode (pick_count: 1) for BYS — picking a scent assigns it to the currently
   * open slot and closes the picker.
   */
  toggleCompanion(event) {
    if (event.target?.closest('[data-aromely-dropdown]')) return;
    const opt = event.target?.closest('[data-product-id]');
    if (!opt) return;

    const productId = parseInt(opt.getAttribute('data-product-id'), 10) || 0;
    const variantId = parseInt(opt.getAttribute('data-variant-id'), 10) || 0;

    const scent = this.#scents.find(s => s.variant_id === variantId)
      || this.#scents.find(s => s.product_id === productId);
    if (!scent) return;

    this.#slots[this.#currentSlotIndex] = scent;
    this.#updateSlotUI(this.#currentSlotIndex);
    this.#updateTotal();
    this.closeSwapDialog();
  }

  /**
   * Keyboard activation for the option div in the dialog.
   * @param {KeyboardEvent} event
   */
  handleOptionKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.target?.closest('[data-aromely-dropdown]')) return;
    event.preventDefault();
    this.toggleCompanion(event);
  }

  /**
   * Confirm button is hidden in single-select mode but the snippet still wires
   * the handler. Mirror closeSwapDialog so unexpected confirm clicks don't 404.
   */
  confirmSelection() {
    this.closeSwapDialog();
  }

  #initDragToClose() {
    const dialog = this.refs.swapDialog;
    if (!dialog) return;
    const handle = dialog.querySelector('[ref="dragHandle"]');
    if (!handle) return;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    const onStart = (e) => {
      isDragging = true;
      startY = e.touches[0].clientY;
      currentY = 0;
      dialog.setAttribute('data-dragging', '');
      dialog.style.transition = 'none';
      dialog.style.transform = 'translateY(0)';
    };

    const onMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      currentY = Math.max(0, y - startY);
      dialog.style.transform = `translateY(${currentY}px)`;
    };

    const onEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      dialog.style.transition = 'transform 0.25s cubic-bezier(0.32, 0.72, 0, 1)';
      if (currentY > 80) {
        dialog.style.transform = 'translateY(100%)';
        setTimeout(() => {
          dialog.close();
          dialog.removeAttribute('data-dragging');
          dialog.style.transform = '';
          dialog.style.transition = '';
          document.body.style.overflow = '';
        }, 250);
      } else {
        dialog.style.transform = 'translateY(0)';
        setTimeout(() => {
          dialog.style.transform = '';
          dialog.style.transition = '';
        }, 250);
      }
    };

    handle.addEventListener('touchstart', onStart, { passive: true });
    handle.addEventListener('touchmove', onMove, { passive: false });
    handle.addEventListener('touchend', onEnd);
  }

  /**
   * The shared snippet emits aromely-select-dropdown elements that the kit
   * controller wires up imperatively. BYS doesn't render those (we pass
   * show_variant_dropdown: false), but if a future caller flips that flag,
   * we still need the open/close behavior. Keep this minimal — same pattern
   * as aromely-pdp-kit.js #initVariantDropdowns, deduplicated.
   */
  #initDropdownPanels() {
    const dropdowns = this.querySelectorAll('[data-aromely-dropdown]');
    if (dropdowns.length === 0) return;

    /** @type {HTMLElement[]} */
    const panels = [];
    for (const dd of dropdowns) {
      if (!(dd instanceof HTMLElement)) continue;
      const trigger = dd.querySelector('[data-dropdown-trigger]');
      const panel = dd.querySelector('[data-dropdown-panel]');
      if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) continue;
      panels.push(panel);

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = panel.getAttribute('aria-hidden') === 'false';
        for (const other of panels) other.setAttribute('aria-hidden', 'true');
        panel.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    }

    document.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      for (const panel of panels) {
        if (panel.getAttribute('aria-hidden') === 'false') {
          const dd = panel.closest('[data-aromely-dropdown]');
          if (dd && !dd.contains(target)) panel.setAttribute('aria-hidden', 'true');
        }
      }
    });
  }

  // ── Slot rendering ────────────────────────────────────────────────────────

  #updateSlotUI(slotIndex) {
    const slotEl = this.refs.slots?.querySelector(`[data-slot-index="${slotIndex}"]`);
    const scent = this.#slots[slotIndex];
    if (!slotEl || !scent) return;

    slotEl.setAttribute('data-product-id', String(scent.product_id));
    slotEl.setAttribute('data-variant-id', String(scent.variant_id));

    const img = slotEl.querySelector('[data-slot-img]');
    if (img instanceof HTMLImageElement) {
      img.src = scent.image || '';
      img.alt = scent.title || '';
      img.classList.toggle('hidden', !scent.image);
    }
    const title = slotEl.querySelector('[data-slot-title]');
    if (title) title.textContent = scent.title;

    // Subtitle = variant title + " • " + subtitle metafield (when product
    // has multiple variants). Same combining logic as the Liquid initial
    // render so swapped slots match server-rendered ones.
    const subtitle = slotEl.querySelector('[data-slot-subtitle]');
    if (subtitle) {
      const showVariant = scent.has_only_default_variant === false;
      const sub = scent.subtitle || '';
      const parts = [];
      if (showVariant && scent.variant_title) parts.push(scent.variant_title);
      if (sub) parts.push(sub);
      subtitle.textContent = parts.join(' • ');
    }

    // Apply the section's fragrance discount + subscription discount so the
    // per-slot price line matches what we show in the running total.
    const orig = scent.price;
    let final = orig;
    if (this.#fragranceDiscountPct > 0) {
      final = Math.round(final * (1 - this.#fragranceDiscountPct / 100));
    }
    // Subscription discount only when this scent is actually subscribable.
    if (this.#subscriptionEnabled && this.#subscriptionDiscountPct > 0 && scent.selling_plan_id) {
      final = Math.round(final * (1 - this.#subscriptionDiscountPct / 100));
    }
    let compareValue = 0;
    if (final < orig) {
      compareValue = orig;
    } else if (scent.compare_at_price > scent.price) {
      compareValue = scent.compare_at_price;
    }

    const price = slotEl.querySelector('[data-slot-price]');
    if (price) price.textContent = this.#formatPrice(final);

    const compare = slotEl.querySelector('[data-slot-compare]');
    if (compare) {
      if (compareValue > 0) {
        compare.textContent = formatMoney(compareValue, this.#moneyFormat, this.#currency);
        compare.classList.remove('hidden');
      } else {
        compare.textContent = '';
        compare.classList.add('hidden');
      }
    }
  }

  // ── Totals ────────────────────────────────────────────────────────────────

  #updateTotal() {
    const diffuser = this.#diffusers[this.#activeDiffuserIndex];
    const variant = diffuser?.variants?.[this.#activeVariantIndex] || diffuser?.variants?.[0];

    if (!variant) {
      if (this.refs.totalPrice) this.refs.totalPrice.textContent = this.#formatPrice(0);
      if (this.refs.totalCompare) this.refs.totalCompare.classList.add('hidden');
      if (this.refs.checkoutBtn) this.refs.checkoutBtn.disabled = true;
      if (this.refs.saveBadge) this.refs.saveBadge.hidden = true;
      return;
    }

    // Subscription discount stacks on top of the per-line discount when the
    // toggle is on.
    const subActive = this.#subscriptionEnabled && this.#subscriptionDiscountPct > 0;
    const subFactor = subActive ? (1 - this.#subscriptionDiscountPct / 100) : 1;

    // Diffuser line: diffuser discount + subscription discount (only if the diffuser is subscribable).
    const diffOrig = variant.price;
    let diffFinal = this.#diffuserDiscountPct > 0
      ? Math.round(diffOrig * (1 - this.#diffuserDiscountPct / 100))
      : diffOrig;
    if (subActive && variant.selling_plan_id) diffFinal = Math.round(diffFinal * subFactor);
    const diffCompare = variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;

    // Fragrance lines: fragrance discount + subscription discount (only for subscribable scents).
    let fragFinalSum = 0;
    let fragCompareSum = 0;
    for (const scent of this.#slots) {
      if (!scent) continue;
      const orig = scent.price;
      let final = this.#fragranceDiscountPct > 0
        ? Math.round(orig * (1 - this.#fragranceDiscountPct / 100))
        : orig;
      if (subActive && scent.selling_plan_id) final = Math.round(final * subFactor);
      fragFinalSum += final;
      fragCompareSum += scent.compare_at_price > scent.price ? scent.compare_at_price : scent.price;
    }

    const discounted = diffFinal + fragFinalSum;
    const compareTotal = diffCompare + fragCompareSum;

    if (this.refs.totalPrice) {
      this.refs.totalPrice.textContent = this.#formatPrice(discounted);
    }
    if (this.refs.totalCompare) {
      if (compareTotal > discounted) {
        this.refs.totalCompare.textContent = formatMoney(compareTotal, this.#moneyFormat, this.#currency);
        this.refs.totalCompare.classList.remove('hidden');
      } else {
        this.refs.totalCompare.classList.add('hidden');
      }
    }

    const savings = Math.max(0, compareTotal - discounted);
    if (this.refs.saveBadge && this.refs.saveAmount) {
      if (savings > 0) {
        this.refs.saveAmount.textContent = formatMoney(savings, this.#moneyFormat, this.#currency);
        this.refs.saveBadge.hidden = false;
      } else {
        this.refs.saveBadge.hidden = true;
      }
    }

    const allSlotsFilled = this.#slots.length === this.#fragranceCount && this.#slots.every(Boolean);
    if (this.refs.checkoutBtn) this.refs.checkoutBtn.disabled = !allSlotsFilled;

    this.#updateSubscriptionBoxVisibility();
  }

  /**
   * Show the subscribe-and-save toggle iff at least one selected line item
   * (diffuser variant + scent slots) actually has a selling plan attached.
   * Avoids showing the toggle on sets where nothing is subscribable.
   */
  #updateSubscriptionBoxVisibility() {
    const box = this.refs.subscriptionBox;
    if (!box || !this.#subscriptionToggleAvailable) return;

    const diffuser = this.#diffusers[this.#activeDiffuserIndex];
    const variant = diffuser?.variants?.[this.#activeVariantIndex] || diffuser?.variants?.[0];
    const hasSubscribable = !!variant?.selling_plan_id
      || this.#slots.some(s => !!s?.selling_plan_id);
    box.hidden = !hasSubscribable;
  }

  /**
   * Bind a direct change listener on the subscription checkbox. The framework's
   * `on:change` delegation didn't reliably fire for this label-wrapped hidden
   * checkbox, so we use a plain listener.
   */
  #initSubscriptionToggle() {
    const checkbox = this.refs.subscriptionCheckbox;
    if (!checkbox) return;
    checkbox.addEventListener('change', (e) => this.toggleSubscription(e));
  }

  /**
   * Subscription toggle change handler — flips state, re-renders price lines
   * (subscription discount stacks on top of regular discounts), and
   * recomputes the total. The selling_plan is attached at cart-add time
   * based on this flag.
   */
  toggleSubscription(event) {
    const checkbox = event?.target;
    if (checkbox instanceof HTMLInputElement) {
      this.#subscriptionEnabled = checkbox.checked;
    }

    // Re-render every filled slot so its price reflects the new toggle state.
    for (let i = 0; i < this.#slots.length; i++) {
      if (this.#slots[i]) this.#updateSlotUI(i);
    }
    this.#updateDiffuserCard(this.#activeDiffuserIndex);
    this.#updateTotal();
  }

  // ── Checkout ──────────────────────────────────────────────────────────────

  checkout(event) {
    event?.preventDefault();
    const btn = this.refs.checkoutBtn;
    if (!btn || btn.disabled || btn.dataset.state === 'loading') return;

    const diffuser = this.#diffusers[this.#activeDiffuserIndex];
    const variant = diffuser?.variants?.[this.#activeVariantIndex] || diffuser?.variants?.[0];
    if (!variant) return;

    btn.dataset.state = 'loading';
    btn.disabled = true;

    // Attach selling_plan per-line only when the subscription toggle is on
    // (or when the toggle is disabled at the section level — default ON).
    // Shopify accepts selling_plan per-item and silently ignores items that
    // don't include it, so the diffuser and scents can each have their own.
    const subscribe = this.#subscriptionToggleAvailable ? this.#subscriptionEnabled : true;

    const diffuserItem = { id: variant.id, quantity: 1 };
    if (subscribe && variant.selling_plan_id) diffuserItem.selling_plan = variant.selling_plan_id;
    const items = [diffuserItem];

    for (const scent of this.#slots) {
      if (!scent) continue;
      const item = { id: scent.variant_id, quantity: 1 };
      if (subscribe && scent.selling_plan_id) item.selling_plan = scent.selling_plan_id;
      items.push(item);
    }

    // Free gift — added to the cart as a regular line item. The $0 price is
    // expected to come from a Buy X Get Y discount configured in Shopify
    // (we don't try to force price in the payload; Shopify rejects that).
    if (this.#gift && this.#gift.variant_id) {
      items.push({ id: this.#gift.variant_id, quantity: 1 });
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then(res => res.json())
      .then(response => {
        if (response.status) {
          console.error('BYS cart add error:', response.description);
          btn.dataset.state = '';
          btn.disabled = false;
          return;
        }

        // Don't dispatch CartAddEvent — that opens the cart drawer. We want
        // the user to go straight to checkout.
        const base = root.replace(/\/$/, '');
        return this.#applyDiscounts(root)
          .then(() => AromelyBuildYourSet.#awaitPromo())
          .then(() => {
          window.location.replace(`${base}/checkout`);
        });
      })
      .catch(err => {
        console.error('BYS cart add error:', err);
        btn.dataset.state = '';
        btn.disabled = false;
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  /**
   * October Sale: lets the promotion runtime put the free samples (or take a
   * stale kit) on the cart before the redirect. Capped at 3 s and never
   * rejecting, so a slow reconcile cannot strand the shopper on this page.
   * @returns {Promise<void>}
   */
  static #awaitPromo() {
    const hook = window.AromelyPromo?.beforeCheckout;
    if (typeof hook !== 'function') return Promise.resolve();
    const timeout = new Promise((resolve) => setTimeout(resolve, 3000));
    return Promise.race([Promise.resolve().then(() => hook()), timeout])
      .then(() => undefined)
      .catch((err) => { console.error('BYS promo beforeCheckout error:', err); });
  }

  /**
   * Splits the comma-separated setting into codes: trimmed, empties dropped,
   * deduped case-insensitively while keeping the first spelling. Shopify treats
   * FBSET10 and fbset10 as the same code, so sending both is pointless noise.
   * @param {string|undefined} raw
   * @returns {string[]}
   */
  static #parseCodes(raw) {
    const seen = new Set();
    const codes = [];
    for (const part of String(raw || '').split(',')) {
      const code = part.trim();
      if (!code) continue;
      const key = code.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      codes.push(code);
    }
    return codes;
  }

  /**
   * Puts every configured code on the cart in one call, then resolves however it
   * goes. This funnel has never verified the outcome and still does not: it does
   * not read the cart back, so a code Shopify refuses is silent here, exactly as
   * it was when a refused code simply arrived at checkout unapplied. The bundle
   * funnel is the one that checks.
   * @param {string} root
   * @returns {Promise<void>}
   */
  #applyDiscounts(root) {
    // October Sale: while it runs, the promotion code replaces this page's own codes.
    const codes = window.AromelyPromo?.codes?.(this.#discountCodes) ?? this.#discountCodes;
    if (!codes.length) return Promise.resolve();
    const updateUrl = window.Theme?.routes?.cart_update_url || `${root}cart/update.js`;
    return fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    })
      .then(() => undefined)
      .catch((err) => {
        // Never strand the shopper on a discount problem — they still reach
        // checkout, just without the codes attached.
        console.error('BYS discount apply error:', err);
      });
  }

  /** @param {string} s */
  #escapeAttr(s) {
    return String(s || '').replace(/"/g, '&quot;');
  }

  /**
   * Format a money value for display. Returns the localized FREE label when
   * the price is zero (e.g. after a 100% fragrance discount), otherwise
   * delegates to the theme's money formatter.
   * @param {number} cents
   */
  #formatPrice(cents) {
    if (!cents || cents <= 0) return this.#freeLabel;
    return formatMoney(cents, this.#moneyFormat, this.#currency);
  }
}

if (!customElements.get('aromely-build-your-set')) {
  customElements.define('aromely-build-your-set', AromelyBuildYourSet);
}
